import {useCallback, useState} from 'react';
import {UrlLink} from '../../../applink/UrlLink';
import {t} from '../../../common/getTranslations';
import {openWindow} from '../../../common/openWindow.function';
import {formatWithCommas} from '../../../common/shared/utils/formatWithCommas';
import {getTicketName} from '../../../domain/buy/ticket/util/getTicketName';
import {getTicketNameForLogging} from '../../../domain/buy/ticket/util/getTicketNameForLogging';
import {
  Maybe,
  SaleMethod,
  SimpleSet,
  TicketInfo,
  TicketKind,
  TicketType,
} from '../../../graphql/generated/types';
import {aggregateAndSendLog} from '../../../logging/aggregateAndSendLog';
import {LOG_CLICK_EVENT} from '../../../logging/logEvent';
import {putTiaraObjectProps} from '../../../logging/putTiaraObjectProps';
import {ButtonV2Variant} from '../../atoms/ButtonV2/ButtonV2.enum';
import {Icon} from '../../atoms/Icon/Icon';
import {IconType} from '../../atoms/Icon/IconType.enum';
import {Text} from '../../atoms/Text/Text';
import {Dialog} from '../Dialog';
import {DialogButtonProps} from '../Dialog/DialogButton';

export interface TicketBuyAndUseDialogProps {
  isMobile: boolean;
  productId: number;
  seriesId: number;
  saleMethod?: Maybe<SaleMethod>;
  nonstopWatching?: boolean;
  title?: string;
  waitfreeBlock?: boolean;
  ticketRentalCount?: number;
  ticketOwnCount?: number;
  cashAmount?: Maybe<number>;
  availableTicketRentalType?: Maybe<TicketType>;
  purchasableTicketRental?: Maybe<TicketInfo>;
  availableTicketOwnType?: Maybe<TicketType>;
  purchasableTicketOwn?: Maybe<TicketInfo>;
  onUseTicket: (params: {ticketType: TicketType; popupOff: boolean}) => void;
  onBuyAndUseTicket: (params: {
    ticketInfo: TicketInfo;
    popupOff: boolean;
  }) => void;
  onClickCancel: () => void;
  triggerByPopupOn?: boolean;
  localStoragePopupOn?: boolean;
  set?: Maybe<SimpleSet>;
  onClickSet?: (params: {setInfo: SimpleSet}) => void;
  isShortsDrama?: boolean;
  rentalPeriodByMinute: number;
}

export function TicketBuyAndUseDialog({
  isMobile,
  productId,
  seriesId,
  saleMethod,
  nonstopWatching,
  title,
  waitfreeBlock,
  ticketRentalCount,
  ticketOwnCount,
  cashAmount,
  availableTicketRentalType,
  purchasableTicketRental,
  availableTicketOwnType,
  purchasableTicketOwn,
  onUseTicket,
  onBuyAndUseTicket,
  onClickCancel,
  triggerByPopupOn,
  set,
  onClickSet,
  isShortsDrama,
  rentalPeriodByMinute,
}: TicketBuyAndUseDialogProps) {
  const isCashType = saleMethod === SaleMethod.Cash;
  const [checkedShouldPopupOff, setCheckedShouldPopupOff] =
    useState<boolean>(false);
  const handleClickPopupOn = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      LOG_CLICK_EVENT('사용확인_다시보지않기_클릭', e, () =>
        setCheckedShouldPopupOff(!checkedShouldPopupOff)
      );
    },
    [checkedShouldPopupOff]
  );
  return (
    <Dialog
      data-test="ticket-dialog"
      isOpen={true}
      onClose={onClickCancel}
      isUsingScrollBlock={false}
      isMobile={isMobile}
      {...putTiaraObjectProps({
        click: {
          layer1: `${isCashType ? '캐시타입' : ''}구매팝업`,
          imp_id: `popup_p${isCashType ? '_cash' : ''}`,
          imp_provider: 'service',
        },
        viewimp_contents: {
          layer1: `${isCashType ? '캐시타입' : ''}구매팝업`,
          imp_id: `popup_p${isCashType ? '_cash' : ''}`,
          imp_provider: 'service',
          imp_type: 'vimp',
        },
        customProps: {
          popup_type: `${isCashType ? '캐시타입' : ''}구매팝업`,
          page_ticket_rental: availableTicketRentalType
            ? getTicketNameForLogging(availableTicketRentalType)
            : purchasableTicketRental
              ? `3일대여_${
                  purchasableTicketRental.discountPrice ??
                  purchasableTicketRental.price
                }캐시`
              : '',
          page_ticket_own: availableTicketOwnType
            ? getTicketNameForLogging(availableTicketOwnType)
            : purchasableTicketOwn
              ? `소장_${
                  purchasableTicketOwn.discountPrice ??
                  purchasableTicketOwn.price
                }캐시`
              : '',
          ...(nonstopWatching !== undefined
            ? {
                viewer_mode: nonstopWatching ? '정주행모드' : '일반모드',
              }
            : {}),
          ...(isShortsDrama && {
            is_shortdrama: isShortsDrama ? 'y' : undefined,
          }),
        },
      })}
      onInView={target => {
        aggregateAndSendLog({
          targetElem: target as HTMLElement,
          actionType: 'ViewImp',
          actionName: '팝업_노출',
          customProps: {
            user_action: 'imp',
            ...(isShortsDrama && {
              is_shortdrama: isShortsDrama ? 'y' : undefined,
            }),
          },
        });
      }}
    >
      <Dialog.Dimmed />
      <Dialog.Title>{title}</Dialog.Title>
      <Dialog.List>
        {waitfreeBlock && (
          <Dialog.Label className="font-small1 text-theme-solid-60 mb-20pxr">
            {t('purchase_ticket_popup_not_wuf_info')}
          </Dialog.Label>
        )}
        <Dialog.Divider />
      </Dialog.List>
      <Dialog.List className="!space-y-8pxr">
        {isCashType && (
          <div className="flex justify-between">
            <Dialog.Label className="text-left font-small1">
              {t('purchase_cash_popup_cash_balance', {
                values: {value: formatWithCommas(cashAmount || 0)},
              })}
            </Dialog.Label>
            <div className="flex space-x-4px">
              <Dialog.Label className="font-small1 text-theme-solid-60">
                {t('purchase_cash_popup_terms_button')}
              </Dialog.Label>
              <Icon
                type={IconType.ContentHomeHelpSolid}
                className="cursor-pointer"
                onClick={e =>
                  LOG_CLICK_EVENT('콘텐츠구매안내_클릭', e, () => {
                    openWindow({
                      url: `https://${window.location.host}/content/policy/cash?rentalPeriodByMinute=${rentalPeriodByMinute}`,
                      target: '콘텐츠 구매',
                      features: 'width=360,height=800',
                    });
                  })
                }
              />
            </div>
          </div>
        )}
        {!isCashType && (
          <>
            <Dialog.Label className="text-left font-small1">
              {t('purchase_ticket_popup_ticket_rental', {
                values: {value: formatWithCommas(ticketRentalCount || 0)},
              })}{' '}
              ·{' '}
              {t('purchase_ticket_popup_ticket_possession', {
                values: {value: formatWithCommas(ticketOwnCount || 0)},
              })}{' '}
              ·{' '}
              {t('purchase_ticket_popup_ticket_cash', {
                values: {value: formatWithCommas(cashAmount || 0)},
              })}
            </Dialog.Label>
            <div className="flex justify-between">
              <UrlLink
                url={`/buy/ticket?seriesId=${seriesId}&productId=${productId}`}
                className="font-small1 flex cursor-pointer items-center text-theme-solid-100"
                onClick={e => LOG_CLICK_EVENT('패키지구매_클릭', e)}
              >
                <Dialog.Label className="font-small1">
                  {t('purchase_ticket_popup_ticket_package_button')}
                </Dialog.Label>
                <Icon className="inline" type={IconType.ButtonArrowS} />
              </UrlLink>
              <div className="flex space-x-4px items-center">
                <Dialog.Label className="font-small1 text-theme-solid-60">
                  {t('purchase_ticket_popup_ticketbuy_info_button')}
                </Dialog.Label>
                <Icon
                  type={IconType.ContentHomeHelpSolid}
                  className="cursor-pointer"
                  onClick={e =>
                    LOG_CLICK_EVENT('이용약관_클릭', e, () => {
                      openWindow({
                        url: `https://${window.location.host}/content/policy/ticket?rentalPeriodByMinute=${rentalPeriodByMinute}`,
                        target: '이용권 구매 안내',
                        features: 'width=360,height=800',
                      });
                    })
                  }
                />
              </div>
            </div>
          </>
        )}
      </Dialog.List>
      <Dialog.List className="px-18pxr pb-10pxr space-y-10pxr">
        {isCashType && set && (
          <Dialog.Button
            data-test="cash-type-set-button"
            variant={ButtonV2Variant.Negative}
            onClick={e =>
              onClickSet &&
              LOG_CLICK_EVENT('세트상품더보기_클릭', e, () =>
                onClickSet({setInfo: set})
              )
            }
          >
            {!set.discountRate && t('contenthome_sethome_button_no_discount')}
            {!!set.discountRate && (
              <div>
                {t('purchase_cash_popup_sethome_button', {
                  values: {value: set.discountRate},
                })}
              </div>
            )}
          </Dialog.Button>
        )}
        {availableTicketRentalType && (
          <Dialog.Button
            {...getUseButtonProps({
              ticketKind: TicketKind.Rent,
              ticketType: availableTicketRentalType,
            })}
          />
        )}
        {!availableTicketRentalType && purchasableTicketRental && (
          <Dialog.Button
            {...getBuyAndUseButtonProps({
              ticketKind: TicketKind.Rent,
              ticketInfo: purchasableTicketRental,
            })}
          />
        )}
        {availableTicketOwnType && (
          <Dialog.Button
            {...getUseButtonProps({
              ticketKind: TicketKind.Own,
              ticketType: availableTicketOwnType,
            })}
          />
        )}
        {!availableTicketOwnType && purchasableTicketOwn && (
          <Dialog.Button
            {...getBuyAndUseButtonProps({
              ticketKind: TicketKind.Own,
              ticketInfo: purchasableTicketOwn,
            })}
          />
        )}
      </Dialog.List>
      <Dialog.List className="pb-0">
        {!isCashType && triggerByPopupOn && (
          <Dialog.CheckListItem
            checked={checkedShouldPopupOff}
            label={t('purchase_ticket_popup_not_ask_again')}
            onChangeChecked={handleClickPopupOn}
            {...putTiaraObjectProps({
              click: {
                copy: checkedShouldPopupOff ? 'off' : 'on',
              },
            })}
          />
        )}
        {isCashType && (
          <Dialog.Caption>{t('purchase_ticket_popup_info')}</Dialog.Caption>
        )}
      </Dialog.List>
      <Dialog.BottomCombination>
        <Dialog.Button
          onClick={e => LOG_CLICK_EVENT('취소_클릭', e, onClickCancel)}
        >
          {t('common_cancel_button')}
        </Dialog.Button>
      </Dialog.BottomCombination>
    </Dialog>
  );
  function getUseButtonProps({
    ticketKind,
    ticketType,
  }: {
    ticketKind: TicketKind;
    ticketType: TicketType;
  }): DialogButtonProps {
    return {
      ...putTiaraObjectProps({
        click: {
          copy: `대여_${getTicketNameForLogging(ticketType)}`,
        },
        customProps: {
          event_series_id: String(seriesId),
          event_episode_id: String(productId),
          event_ticket_type: `대여_${getTicketNameForLogging(ticketType)}`,
        },
      }),
      onClick: e =>
        LOG_CLICK_EVENT(
          `${ticketKind === TicketKind.Own ? '소장' : '대여'}_클릭`,
          e,
          () => onUseTicket({ticketType, popupOff: checkedShouldPopupOff})
        ),
      children: isTicketPurchased(ticketType)
        ? ticketKind === TicketKind.Own
          ? isCashType
            ? t('common_content_possession')
            : t('common_ticket_possession')
          : isCashType
            ? t('common_ticket_rental_cash')
            : t('common_ticket_rental')
        : getTicketName(ticketType),
      variant: !isCashType ? ButtonV2Variant.Static : ButtonV2Variant.Primary,
    };
  }

  function getBuyAndUseButtonProps({
    ticketKind,
    ticketInfo,
  }: {
    ticketKind: TicketKind;
    ticketInfo: TicketInfo;
  }): DialogButtonProps {
    const rentalPeriodDate = ticketInfo.rentalPeriodByMinute
      ? formatWithCommas(ticketInfo.rentalPeriodByMinute / (60 * 24))
      : '3';

    return {
      description: (
        <Text>
          {ticketInfo?.discountPrice && (
            <>
              <Text className="text-theme-solid-60 line-through">
                {formatWithCommas(ticketInfo.price)}
              </Text>
              &nbsp;
            </>
          )}
          <Text>
            {t('purchase_cash_popup_cash_button', {
              values: {
                value: formatWithCommas(
                  ticketInfo.discountPrice ?? ticketInfo.price
                ),
              },
            })}
          </Text>
        </Text>
      ),
      ...putTiaraObjectProps({
        click: {
          copy: `${ticketKind === TicketKind.Own ? '소장' : '대여'}_${
            ticketInfo.discountPrice ?? ticketInfo.price
          }캐시`,
        },
        customProps: {
          event_series_id: String(seriesId),
          event_episode_id: String(productId),
          event_ticket_type: `${
            ticketKind === TicketKind.Own ? '소장' : '대여'
          }_${ticketInfo.discountPrice ?? ticketInfo.price}캐시`,
        },
      }),
      onClick: e =>
        LOG_CLICK_EVENT(
          `${ticketKind === TicketKind.Own ? '소장' : '대여'}_클릭`,
          e,
          () => onBuyAndUseTicket({ticketInfo, popupOff: checkedShouldPopupOff})
        ),
      children:
        ticketKind === TicketKind.Own
          ? isCashType
            ? t('common_content_possession')
            : t('common_ticket_possession')
          : isCashType
            ? t('purchase_cash_popup_rent', {
                values: {
                  value: t('common_period_day', {
                    values: {
                      value: rentalPeriodDate,
                    },
                  }),
                },
              })
            : t('purchase_ticket_popup_rental_button', {
                values: {
                  value: t('common_period_day', {
                    values: {
                      value: rentalPeriodDate,
                    },
                  }),
                },
              }),
      variant: ButtonV2Variant.Primary,
    };
  }
}
/**
 * 구매한 대여권, 소장권이 있다면 텍스트 하단에 이용권명 노출하지 않음(기획)
 * @param ticketType
 */
function isTicketPurchased(ticketType: TicketType) {
  return purchasedTicketType.includes(ticketType);
}
const purchasedTicketType = [
  TicketType.BuySingle,
  TicketType.BuyPackage,
  TicketType.BuyMoviePpv,
  TicketType.RentSingle,
  TicketType.RentPackage,
  TicketType.RentMoviePpv,
];
